import { CacheProvider } from '@emotion/react';
import { Toaster } from '@fluentui/react-components';
import { BaselineStyles, ThemeProvider, jdLightTheme } from '@galaxy-react/core';
import { AuthAgent } from '@leapdev/auth-agent';
import { UserInfo } from '@leapdev/auth-agent/src/lib/types';
import { createContext, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import eventEmitter from 'src/Shared/Utilities/eventEmitter';
import { TOAST_ID, useToast } from 'src/feature/Reports/Hooks/useToast';
import 'src/styles/index.css';

import createCache from '@emotion/cache';

import { setSentryUserContext } from '@/sentry';
import { db } from 'src/services/db';
import router from './router';
import { store } from './store';

export const UserInfoContext = createContext<Partial<UserInfo> | undefined>({});
const cache = createCache({
  key: 'reporting',
});

function App() {
  const { notify } = useToast();

  useEffect(() => {
    eventEmitter.on('toast', notify);
    return () => {
      eventEmitter.off('toast', notify);
    };
  }, [notify]);

  const [userInfo, setUserInfo] = useState<UserInfo>();

  useEffect(() => {
    AuthAgent.userInfo().then((v) => {
      setUserInfo(v);
      setSentryUserContext(v);
    });
  }, []);

  // Reconcile any cancellation markers left during page close
  // Fallback logic for when the service worker is not available
  useEffect(() => {
    const listKey = 'downloadCancelIds';
    try {
      const raw = localStorage.getItem(listKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      if (ids.length) {
        void Promise.all(ids.map((id) => db.downloadList.update(id, { status: 'CANCELLED' }))).finally(() => {
          localStorage.removeItem(listKey);
        });
      }
    } catch (_) {
      localStorage.removeItem(listKey);
    }
  }, []);

  return (
    <Provider store={store}>
      <Toaster toasterId={TOAST_ID} />
      <CacheProvider value={cache}>
        {/* Do not remove this provider, otherwise the styles will be inserted in the wrong order */}
        <ThemeProvider theme={jdLightTheme}>
          <BaselineStyles />
          <UserInfoContext.Provider value={userInfo}>
            {userInfo && <RouterProvider router={router} />}
          </UserInfoContext.Provider>
        </ThemeProvider>
      </CacheProvider>
    </Provider>
  );
}

export default App;
